/**
 * Client portal form/assessment submission via the form-submission bot.
 *
 * Patients must not write QuestionnaireResponse resources directly — the bot
 * validates the open Task, applies compartment meta, and completes the QR.
 */
import { ContentType } from '@medplum/core';
import type { MedplumClient } from '@medplum/core';
import type { Extension, Identifier, QuestionnaireResponse, Task } from '@medplum/fhirtypes';
import { CLIENT_SIGNATURE_EXT_URL } from '../pages/appointments/fhirNoteMapping';

export const FORM_SUBMISSION_BOT_IDENTIFIER = {
  system: 'https://unify.health/bots',
  value: 'form-submission',
} as const;

export const CONSENT_BOT_IDENTIFIER = {
  system: 'https://unify.health/bots',
  value: 'consent',
} as const;

export interface PortalFormSubmissionResult {
  questionnaireResponseId: string;
  taskId: string;
  patientId?: string;
}

export type SessionRecordingConsentMutationResult = {
  consentId: string;
  questionnaireResponseId?: string;
};

function resolveBotExecuteTarget(
  botIdEnvKey: keyof ImportMetaEnv,
  systemEnvKey: keyof ImportMetaEnv,
  valueEnvKey: keyof ImportMetaEnv,
  fallback: Identifier,
): string | Identifier {
  const id = import.meta.env[botIdEnvKey]?.trim();
  if (id) {
    return id;
  }
  const system = import.meta.env[systemEnvKey]?.trim() ?? fallback.system;
  const value = import.meta.env[valueEnvKey]?.trim() ?? fallback.value;
  return { system, value };
}

export function getFormSubmissionBotExecuteTarget(): string | Identifier {
  return resolveBotExecuteTarget(
    'VITE_MEDPLUM_FORM_SUBMISSION_BOT_ID',
    'VITE_MEDPLUM_FORM_SUBMISSION_BOT_IDENTIFIER_SYSTEM',
    'VITE_MEDPLUM_FORM_SUBMISSION_BOT_IDENTIFIER_VALUE',
    FORM_SUBMISSION_BOT_IDENTIFIER,
  );
}

/**
 * Minimal QR payload for bot submission — answers (+ optional client signature).
 * Status, subject, meta, and chart workflow extensions are applied server-side.
 */
export function buildPortalFormSubmissionDraft(
  qr: QuestionnaireResponse,
): Partial<QuestionnaireResponse> {
  const extensions: Extension[] = [];
  for (const ext of qr.extension ?? []) {
    if (ext.url === CLIENT_SIGNATURE_EXT_URL) {
      extensions.push(ext);
    }
  }

  return {
    resourceType: 'QuestionnaireResponse',
    item: qr.item,
    ...(extensions.length > 0 ? { extension: extensions } : {}),
  };
}

/**
 * Tasks this session submitted. The submission bots write only the QR; `task-events` closes the Task a moment later
 * (ENG-3764 D29), so the portal lists hide these until the Task leaves the open list. A closed Task never reopens, so
 * the ids are never removed.
 */
const submittedPortalTaskIds = new Set<string>();

/** Drop Tasks this session already submitted (still open until `task-events` completes them). */
export function withoutSubmittedPortalTasks<T extends { id?: string }>(tasks: T[]): T[] {
  return tasks.filter((task) => !task.id || !submittedPortalTaskIds.has(task.id));
}

export async function submitClientPortalFormTask(
  medplum: MedplumClient,
  params: {
    task: Task;
    questionnaireResponse: QuestionnaireResponse;
  },
): Promise<PortalFormSubmissionResult> {
  const taskId = params.task.id;
  if (!taskId) {
    throw new Error('Task is missing an id.');
  }

  const result = (await medplum.executeBot(
    getFormSubmissionBotExecuteTarget(),
    {
      taskId,
      questionnaireResponse: buildPortalFormSubmissionDraft(params.questionnaireResponse),
    },
    ContentType.JSON,
  )) as PortalFormSubmissionResult;
  submittedPortalTaskIds.add(taskId);
  return result;
}

export async function revokeSessionRecordingConsent(
  medplum: MedplumClient,
  params: { targetPatientId: string },
): Promise<SessionRecordingConsentMutationResult> {
  return medplum.executeBot(
    CONSENT_BOT_IDENTIFIER,
    {
      action: 'session-recording-revoke',
      targetPatientId: params.targetPatientId,
    },
    ContentType.JSON,
  ) as Promise<SessionRecordingConsentMutationResult>;
}

export async function revokeAiToolsConsent(
  medplum: MedplumClient,
  params: { targetPatientId: string },
): Promise<SessionRecordingConsentMutationResult> {
  return medplum.executeBot(
    CONSENT_BOT_IDENTIFIER,
    {
      action: 'ai-tools-consent-revoke',
      targetPatientId: params.targetPatientId,
    },
    ContentType.JSON,
  ) as Promise<SessionRecordingConsentMutationResult>;
}

export async function optInAiToolsConsent(
  medplum: MedplumClient,
  params: {
    targetPatientId: string;
    signature: { name?: string; signatureData: string; signedAt?: string };
  },
): Promise<SessionRecordingConsentMutationResult> {
  return medplum.executeBot(
    CONSENT_BOT_IDENTIFIER,
    {
      action: 'ai-tools-consent-opt-in',
      targetPatientId: params.targetPatientId,
      signature: params.signature,
    },
    ContentType.JSON,
  ) as Promise<SessionRecordingConsentMutationResult>;
}

export async function optInSessionRecordingConsent(
  medplum: MedplumClient,
  params: {
    targetPatientId: string;
    signature: { name?: string; signatureData: string; signedAt?: string };
  },
): Promise<SessionRecordingConsentMutationResult> {
  return medplum.executeBot(
    CONSENT_BOT_IDENTIFIER,
    {
      action: 'session-recording-opt-in',
      targetPatientId: params.targetPatientId,
      signature: params.signature,
    },
    ContentType.JSON,
  ) as Promise<SessionRecordingConsentMutationResult>;
}
